import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckCheck, Settings } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Skeleton } from '@/components/ui/Skeleton'
import { cn } from '@/lib/cn'
import { NotificationItem } from './components/NotificationItem'
import {
  useBulkMarkRead,
  useMarkAllRead,
  useNotifications,
} from '@/hooks/useNotifications'
import type { Notification, NotificationKind } from './types'

const KIND_LABEL: Record<NotificationKind, string> = {
  alert_assigned: 'Alert assigned',
  alert_status_changed: 'Alert status',
  sla_warning: 'SLA warning',
  block_request_pending: 'Block pending',
  block_approved: 'Block approved',
  block_rejected: 'Block rejected',
  case_assigned: 'Case assigned',
  case_unassigned: 'Case unassigned',
  sar_filed: 'SAR filed',
  mention: 'Mention',
  system: 'System',
}

const KINDS = Object.keys(KIND_LABEL) as NotificationKind[]

/**
 * AF-134 — full notifications page.
 *
 *   - Filter by kind (multi-select chips) and read state
 *     (all / unread / read).
 *   - Bulk-select with checkboxes; "Mark selected as read" lights up
 *     when 1+ rows are picked.
 *   - Infinite scroll via React Query infinite-query "Load more" CTA
 *     at the bottom of the page.
 */
export function NotificationsCenterPage() {
  const [filterKinds, setFilterKinds] = useState<NotificationKind[]>([])
  const [readState, setReadState] = useState<'all' | 'unread' | 'read'>('all')
  const [selected, setSelected] = useState<Set<string>>(() => new Set())

  const filters = useMemo(
    () => ({
      kind: filterKinds.length > 0 ? filterKinds : undefined,
      read_state: readState === 'all' ? undefined : readState,
      limit: 20,
    }),
    [filterKinds, readState],
  )

  const list = useNotifications(filters)
  const markAll = useMarkAllRead()
  const bulk = useBulkMarkRead()

  const items: Notification[] = useMemo(
    () => list.data?.pages.flatMap((p) => p.notifications) ?? [],
    [list.data],
  )

  const toggleKind = (k: NotificationKind) =>
    setFilterKinds((cur) =>
      cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k],
    )

  const toggleSelected = (id: string) =>
    setSelected((cur) => {
      const next = new Set(cur)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const submitBulk = () => {
    if (selected.size === 0) return
    bulk.mutate(
      { ids: [...selected] },
      { onSuccess: () => setSelected(new Set()) },
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Notifications</h1>
          <p className="text-sm text-muted-fg">
            Real-time awareness of assignments, SLA warnings, and block
            approvals.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="sm">
            <Link to="/notifications/preferences" className="gap-1">
              <Settings size={14} aria-hidden /> Preferences
            </Link>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => markAll.mutate()}
            disabled={markAll.isPending}
          >
            <CheckCheck size={14} aria-hidden /> Mark all read
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-fg">
              Show
            </span>
            {(['all', 'unread', 'read'] as const).map((s) => (
              <button
                key={s}
                type="button"
                aria-pressed={readState === s}
                onClick={() => setReadState(s)}
                className={cn(
                  'rounded-full border px-2.5 py-1 text-xs capitalize',
                  readState === s
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-fg hover:bg-muted',
                )}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-fg">
              Kinds
            </span>
            {KINDS.map((k) => {
              const isActive = filterKinds.includes(k)
              return (
                <button
                  key={k}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => toggleKind(k)}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-xs',
                    isActive
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-muted-fg hover:bg-muted',
                  )}
                >
                  {KIND_LABEL[k]}
                </button>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {selected.size > 0 && (
        <div className="sticky top-0 z-10 flex items-center justify-between rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
          <span>{selected.size} selected</span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSelected(new Set())}
            >
              Clear selection
            </Button>
            <Button size="sm" onClick={submitBulk} disabled={bulk.isPending}>
              <CheckCheck size={14} aria-hidden /> Mark selected as read
            </Button>
          </div>
        </div>
      )}

      {list.isLoading && (
        <div className="flex flex-col gap-1">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      )}
      {list.isError && (
        <p className="rounded-md border border-status-failed/30 bg-status-failed/5 p-3 text-sm text-status-failed">
          Couldn't load notifications.
        </p>
      )}
      {list.data && items.length === 0 && (
        <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-fg">
          No notifications match the current filters.
        </p>
      )}

      <ul className="flex flex-col gap-1">
        {items.map((n) => (
          <li
            key={n.id}
            className="flex items-start gap-2 rounded border border-border bg-card pl-2"
          >
            <input
              type="checkbox"
              aria-label={`Select ${n.title}`}
              checked={selected.has(n.id)}
              onChange={() => toggleSelected(n.id)}
              className="mt-3"
            />
            <div className="flex-1">
              <NotificationItem notification={n} />
            </div>
          </li>
        ))}
      </ul>

      {list.hasNextPage && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void list.fetchNextPage()}
            disabled={list.isFetchingNextPage}
          >
            {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}
    </div>
  )
}
